import { Injectable } from '@angular/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { race, Subject } from 'rxjs';
import { getOrElse } from 'src/util/values';
import { distinctUntilKeyChanged, takeUntil } from 'rxjs/operators';
import { untilDestroyed } from '@ngneat/until-destroy';
import { OperationAlertComponent } from './operation-alert/operation-alert.component';

export type OperationAlertType = 'executing' | 'success' | 'warning' | 'error';

export type OperationActionType = 'cancelled' | 'closed';
export interface OperationAction {
  type: OperationActionType;
}

export interface MessageParams {
  title?: string;
  message?: string;
  details?: string | object;
  timeoutInSeconds?: number;
}

export interface OperationAlertOptions {
  description: string;
  verbosity: OperationAlertType;
  operationId: string;
}

const configByType: {
  [alertType in OperationAlertType]: {
    timeoutInSeconds?: number;
    title?: string;
    enableClose: boolean;
    verbosityInclude: OperationAlertType[];
  };
} = {
  executing: {
    enableClose: true,
    verbosityInclude: ['executing', 'success', 'warning', 'error'],
  },
  success: {
    timeoutInSeconds: 2,
    enableClose: true,
    verbosityInclude: ['success', 'error', 'warning'],
  },
  warning: {
    title: 'Warning',
    timeoutInSeconds: 5,
    enableClose: true,
    verbosityInclude: ['error', 'warning'],
  },
  error: {
    title: 'Error',
    timeoutInSeconds: 7,
    enableClose: true,
    verbosityInclude: ['error'],
  },
};

export class OperationAlertHandler {
  private _action$ = new Subject<OperationAction>();
  private _toast: ActiveToast<OperationAlertComponent> | null = null;
  private _onDestroy$ = new Subject();
  private _message?: MessageParams;
  get cmp(): OperationAlertComponent | null {
    if (this._toast != null) {
      return this._toast.toastRef.componentInstance;
    }
    return null;
  }

  get onAction$() {
    return this._action$.asObservable();
  }

  constructor(public readonly options: OperationAlertOptions, private _toastr: ToastrService) {}

  public markAsSuccess() {
    this._setType('success');
  }
  public markAsError(params?: MessageParams) {
    if (params != null) {
      if (params.title == null) {
        params.title = 'Error: ' + this.options.description;
      }
      if (params.message == null) {
        if (params.details != null) {
          params.message = this.formatErrorMessage(params.details as string);
        }
      }
    }
    this._setType('error', params);
  }
  public markAsExecuting() {
    this._setType('executing');
  }
  public markAsWarning(params?: MessageParams) {
    this._setType('warning', params);
  }

  private _setType(type: OperationAlertType, params: MessageParams = {}) {
    const config = configByType[type];
    if (config.verbosityInclude.includes(type)) {
      params.message = getOrElse(params.message, this.options.description);
      params.title = getOrElse(params.title, config.title);
      if (this._toast == null) {
        this._toast = this._toastr.show(
          params.message,
          params.title,
          {
            disableTimeOut: true,
            closeButton: config.enableClose,
            tapToDismiss: false,
            enableHtml: true,
            progressAnimation: 'increasing',
            progressBar: true,
            toastComponent: OperationAlertComponent,
            positionClass: 'toast-bottom-right',
          },
          type,
        );

        const destroy$ = race(this._toast.onHidden, this._onDestroy$);
        this._toast.toastRef.componentInstance.onAction$
          .pipe(
            distinctUntilKeyChanged('type'),
            untilDestroyed(this, 'destroy'),
            takeUntil(destroy$),
          )
          .subscribe(this._action$);
      }

      const cmp = this._toast.toastRef.componentInstance;
      if (cmp.operationId == null) {
        cmp.operationId = this.options.operationId;
      }

      cmp.update(type, params, config.enableClose);

      const timeoutInSeconds =
        params.timeoutInSeconds != null ? params.timeoutInSeconds : config.timeoutInSeconds;
      if (timeoutInSeconds != null && timeoutInSeconds > 0) {
        setTimeout(() => {
          if (cmp.toastPackage.toastType === type && this._toast != null) {
            this._toast.toastRef.close();
          }
        }, timeoutInSeconds * 1000);
      }
    }
  }

  destroy() {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }

  private formatErrorMessage(message: string) {
    if (message.toString().indexOf('Error:') === 0) {
      message = message.toString().substring(7);
    }

    if (message.toString().length > 80) {
      return message.toString().substring(0, 80) + '...';
    } else {
      return message;
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class OperationAlertService {
  constructor(private _toastr: ToastrService) {}

  public createAlert(options: OperationAlertOptions): OperationAlertHandler {
    return new OperationAlertHandler(options, this._toastr);
  }
}
